import React, { useCallback, useState, ReactNode, CSSProperties } from 'react';
import classNames from 'classnames';
import usePastTime from 'hooks/usePastTime';
import PopNotification, {
  PopNotificationProps,
} from 'providers/PopNotifications/components/PopNotification';
import { NotificationType } from 'models/Notification';
import { Button, Loader } from 'ncoded-component-library';

import './PopNotificationCard.styles.scss';

export type PopNotificationCardProps = {
  className?: string;
  notification: NotificationType;
  ctaText: string;
  ctaIcon: ReactNode;
  contentText: string;
} & PopNotificationProps;

export const DEFAULT_ANIMATION_TIME_MS = 20000;

const PopNotificationCard: React.FC<PopNotificationCardProps> = (props) => {
  const {
    className,
    notification,
    ctaText,
    ctaIcon,
    contentText,
    onClose,
    onClick,
    onCtaClick,
    dissappearAfter = DEFAULT_ANIMATION_TIME_MS,
    ...rest
  } = props;

  const classes = classNames('pop-notification-card', className);

  const [isLoading, setIsLoading] = useState(false);

  const { createdAt } = notification || {};

  const pastTime = usePastTime();

  const handleOnCtaClick = useCallback(
    async (e) => {
      e.preventDefault();

      if (isLoading) return;

      try {
        setIsLoading(true);
        await onCtaClick();
      } catch (e) {
        onClose();
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, onClose, onCtaClick],
  );

  return (
    <PopNotification
      {...rest}
      className={classes}
      onClose={onClose}
      onClick={onClick}
    >
      <div className="pop-notification-card__content">
        <p className="pop-notification-card__content__text">{contentText}</p>
        <div className="pop-notification-card__content__action">
          {onCtaClick && (
            <Button
              className={classNames('pop-notification-card__cta', {
                'pop-notification-card__cta__disabled': isLoading,
              })}
              onClick={handleOnCtaClick}
            >
              {ctaText}
              {ctaIcon}
              {isLoading && <Loader size="small" />}
            </Button>
          )}
          <p className="pop-notification-card__content__time">
            {pastTime(createdAt)}
          </p>
        </div>
      </div>
      <div
        style={
          {
            '--animation-duration': `${dissappearAfter}ms`,
          } as CSSProperties
        }
        className="progress-bar-not"
      />
    </PopNotification>
  );
};

export default PopNotificationCard;
